
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from "@mui/material";
import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints";

const ANY_OPTION_MAP = {
  sex: { value: "Any Gender", label: "Any Gender" },
  ethnicity: { value: "Any Ethnicity", label: "Any Ethnicity" },
};

const applySelectionLogic = (current = [], incoming = [], options = [], name) => {
  if (name === "location") {
    if (incoming.includes("NW")) return ["NW"];
    return incoming.filter((value) => value !== "NW");
  }

  const anyOption = ANY_OPTION_MAP[name];

  if (anyOption && incoming.includes(anyOption.value)) {
    return [
      anyOption.value,
      ...options
        .map((option) => option.value)
        .filter((value) => value !== anyOption.value),
    ];
  }

  if (
    anyOption &&
    current.includes(anyOption.value) &&
    !incoming.includes(anyOption.value)
  ) {
    return incoming;
  }

  return incoming;
};

const MultiSelect = ({
  label,
  name,
  value = [],
  options = [],
  onChange,
}) => {
  const anyOption = ANY_OPTION_MAP[name];
  const displayOptions = anyOption ? [anyOption, ...options] : options;
  const selectedValues = Array.isArray(value) ? value : [];

  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={selectedValues}
        onChange={(event) => {
          const nextValue = applySelectionLogic(
            selectedValues,
            event.target.value,
            options,
            name
          );

          onChange(name, nextValue);
        }}
        renderValue={(selected) => {
          if (anyOption && selected.includes(anyOption.value)) {
            return anyOption.label;
          }

          return options
            .filter((option) => selected.includes(option.value))
            .map((option) => option.label)
            .join(", ");
        }}
MenuProps={{
  PaperProps: {
    sx: {
      maxHeight: 300,
      marginTop: 1,
      ...(name !== "location" && name !== "payType"
        ? {
            "& .MuiList-root": {
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 0,
            },
            "& .MuiMenuItem-root": {
              minWidth: 0,
              paddingRight: 1,
            },
          }
        : {}),
    },
  },
}}
      >
        {displayOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedValues.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default function SearchFilters({
  filters,
  setFilters,
  locationOptions = [],
  ethnicityOptions = [],
  unionOptions = [],
  categoryOptions = [],
  roleTypeOptions = [],
  sexOptions = [],
  paymentTypeOptions = [],
  onSearch,
  onClear,
}) {
  const email = localStorage.getItem("email") || "";

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const update = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const ageMin = filters.ageMin ?? 0;
  const ageMax = filters.ageMax ?? 100;

  const ageRangeError =
    ageMin !== "" &&
    ageMax !== "" &&
    Number(ageMax) <= Number(ageMin);

  // Location labels are formatted in CastingNoticesPage.
  // Keep Nationwide at the top of the dropdown.
  const sortedLocationOptions = useMemo(
    () => [
      ...locationOptions.filter((option) => option.value === "NW"),
      ...locationOptions.filter((option) => option.value !== "NW"),
    ],
    [locationOptions]
  );

  // Load the user's saved search.
  useEffect(() => {
    if (!email) return;

    const loadSavedSearch = async () => {
      try {
        const response = await api.get(
          ENDPOINTS.getCastingSearch(),
          {
            params: { email },
          }
        );

        const saved = response.data?.data;

        if (!saved) return;

        setFilters((previous) => ({
          ...previous,
          location: saved.locations || [],
          ethnicity: saved.ethnicities || [],
          unionStatus: saved.unions || [],
          jobCategories: saved.jobCategories || [],
          roleTypes: saved.roleTypes || [],
          sex: saved.sex || [],
          payType: saved.payLevels || [],
          ageMin: saved.minAge ?? 0,
          ageMax: saved.maxAge ?? 100,
        }));

        setToast({
          open: true,
          message: "Loaded your saved search",
          severity: "info",
        });
      } catch (error) {
        console.error("Failed to load saved search", error);
      }
    };

    loadSavedSearch();
  }, [email, setFilters]);

  // Save the current search.
  const saveCastingSearch = async () => {
    if (!email) {
      setToast({
        open: true,
        message: "Please sign in to save your search",
        severity: "warning",
      });
      return;
    }

    const cleanValues = (selected = [], options = [], anyLabel) => {
      if (!Array.isArray(selected)) return [];

      if (selected.includes(anyLabel)) {
        return [
          anyLabel,
          ...options
            .map((option) => option.value)
            .filter((value) => value !== anyLabel),
        ];
      }

      return selected;
    };

    try {
      const selectedPayTypes = Array.isArray(filters.payType)
        ? filters.payType
        : [];

      const payload = {
        email,
        locations: filters.location || [],
        ethnicities: cleanValues(
          filters.ethnicity,
          ethnicityOptions,
          "Any Ethnicity"
        ),
        unions: filters.unionStatus || [],
        jobCategories: filters.jobCategories || [],
        payLevels: selectedPayTypes.includes("Any")
          ? paymentTypeOptions
              .filter((option) => option.value !== "Any")
              .map((option) => option.value)
          : selectedPayTypes,
        sex: cleanValues(
          filters.sex,
          sexOptions,
          "Any Gender"
        ),
        roleTypes: filters.roleTypes || [],
        jobType: filters.jobType || [],
        title: "",
        minAge:
          filters.ageMin === "" || filters.ageMin == null
            ? null
            : Number(filters.ageMin),
        maxAge:
          filters.ageMax === "" || filters.ageMax == null
            ? null
            : Number(filters.ageMax),
        sortOrder: "",
        rushCall: false,
      };

      await api.post(
        ENDPOINTS.saveCastingSearch(),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setToast({
        open: true,
        message: "Search saved successfully",
        severity: "success",
      });
    } catch (error) {
      console.error(
        "Save failed:",
        error.response?.data || error
      );

      setToast({
        open: true,
        message: "Failed to save search",
        severity: "error",
      });
    }
  };

  const closeToast = () => {
    setToast((previous) => ({
      ...previous,
      open: false,
    }));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Location"
          name="location"
          value={filters.location}
          options={sortedLocationOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Categories"
          name="jobCategories"
          value={filters.jobCategories}
          options={categoryOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Union Status"
          name="unionStatus"
          value={filters.unionStatus}
          options={unionOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Role Type"
          name="roleTypes"
          value={filters.roleTypes}
          options={roleTypeOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Gender"
          name="sex"
          value={filters.sex}
          options={sexOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Ethnicity"
          name="ethnicity"
          value={filters.ethnicity}
          options={ethnicityOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Box display="flex" gap={2}>
          <TextField
            label="Min Age"
            type="number"
            size="small"
            fullWidth
            value={ageMin}
            error={ageRangeError}
            inputProps={{ min: 0, max: 100 }}
            onChange={(event) => {
              const rawValue = event.target.value;

              update(
                "ageMin",
                rawValue === ""
                  ? ""
                  : Math.max(
                      0,
                      Math.min(100, Number(rawValue))
                    )
              );
            }}
          />

          <TextField
            label="Max Age"
            type="number"
            size="small"
            fullWidth
            value={ageMax}
            error={ageRangeError}
            helperText={
              ageRangeError
                ? "Max Age must be greater than Min Age"
                : ""
            }
            inputProps={{ min: 0, max: 100 }}
            onChange={(event) => {
              const rawValue = event.target.value;

              update(
                "ageMax",
                rawValue === ""
                  ? ""
                  : Math.max(
                      0,
                      Math.min(100, Number(rawValue))
                    )
              );
            }}
          />
        </Box>
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Payment Type"
          name="payType"
          value={filters.payType || []}
          options={paymentTypeOptions}
          onChange={update}
        />
      </Grid>

      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="flex-end"
        gap={1}
      >
        <Button
          variant="outlined"
          onClick={() => {
            if (typeof onClear === "function") {
              onClear();
            }
          }}
        >
          Clear
        </Button>

        <Button
          variant="outlined"
          onClick={saveCastingSearch}
          disabled={ageRangeError}
        >
          Save Search
        </Button>

        <Button
          variant="contained"
          disabled={ageRangeError}
          onClick={() => {
            if (typeof onSearch === "function") {
              onSearch();
            }
          }}
        >
          Search
        </Button>
      </Grid>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={closeToast}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}
